import { useState, useEffect, useRef, type MutableRefObject, type ReactNode } from 'react';
import setupLightbox from '@/utils/gallery/lightbox.js';

export interface ImageGalleryProps {
    id: string;
    images?: readonly string[];
    altText: string;
    openGallery: boolean;
    imageRef: MutableRefObject<HTMLDivElement | null>;
    galleryValue: number;
}

const hasImages = (images: readonly string[]): number => images?.length;

const renderLightboxImages = (images: readonly string[], altText: string): ReactNode =>
    images.map(
        (image: string): ReactNode => (
            <img key={`lightbox-image-${image}`} src={image} data-src={image} data-gallerylightbox-name="ad" alt={altText} />
        ),
    );

const renderHiddenImages = (images: readonly string[], hotelName: string): ReactNode =>
    images.map((image) => <img key={`hidden-image-${image}`} data-src={image} data-gallerylightbox-name="ad" alt={hotelName} />);

export const ImageGallery = ({ id, images, altText, openGallery, imageRef, galleryValue }: ImageGalleryProps) => {
    const [loadGallery, setLoadGallery] = useState(openGallery);
    const ele = useRef<HTMLDivElement | null>(null);
    useEffect((): void => {
        if (imageRef.current) {
            setupLightbox(imageRef.current);
        }
        if (openGallery) {
            setupLightbox(ele.current);
            if (ele.current && galleryValue === 0) {
                ele.current.click();
            }
        }
        setLoadGallery(false);
    }, [galleryValue, imageRef, openGallery]);

    return (
        <div data-testid="imageGallery" tabIndex={-1}>
            <div ref={ele} data-gallerylightbox-name="ad" data-gallerylightbox-context="mobile" id={`imageGallery_${id}`}>
                {loadGallery ? (
                    <div className="u-display-none" data-testid="galleryLoad">
                        {hasImages(images) ? renderLightboxImages(images, altText) : null}
                    </div>
                ) : (
                    <div className="u-display-none">{hasImages(images) ? renderHiddenImages(images, altText) : null}</div>
                )}
            </div>
        </div>
    );
};
